import React, { useRef, useState } from 'react';
import styles from './Dropdown.module.css';
import DropdownButton, { DropdownButtonProps } from './DropdownButton';
import DropdownButtonWithIcon from './DropdownButtonWithIcon';
import { DropdownContext, DropdownContextValue } from './DropdownContext';
import DropDownInput, { DropdownInputProps } from './DropDownInput';
import DropdownMenu, { DropdownMenuProps } from './DropdownMenu';
import DropdownMenuItem, {
  DropdownMenuItemProps,
  DropdownMenuItemType,
} from './DropdownMenuItem';
import DropdownMenuItemDivider from './DropdownMenuItemDivider';

type DropdownChildrenProps =
  | DropdownButtonProps
  | DropdownInputProps
  | DropdownMenuProps
  | DropdownMenuItemProps;

type DropdownProps = {
  children:
    | React.ReactElement<DropdownChildrenProps>
    | React.ReactElement<DropdownChildrenProps>[];
  usePortal?: boolean;
};

const Dropdown = ({ children, usePortal = false }: DropdownProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
  });
  const [show, setShow] = useState(false);

  const hideMenu = () => {
    setTimeout(() => {
      setShow(false);
    }, 100);
  };

  const showMenu = () => {
    setTimeout(() => {
      setShow(true);
    }, 100);
  };

  const onToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!show) {
      const rect = event.currentTarget.getBoundingClientRect();

      const buttonHeight = buttonRef.current?.offsetHeight || 0;
      const buttonWidth = buttonRef.current?.offsetWidth || 0;
      const menuHeight = menuRef.current?.offsetHeight || 0;
      const menuWidth = menuRef.current?.offsetWidth || 0;

      const top =
        rect.y + menuHeight + buttonHeight > window.innerHeight
          ? rect.y - menuHeight
          : rect.y + buttonHeight;

      const left =
        rect.x + menuWidth > window.innerWidth
          ? rect.x + buttonWidth - menuWidth
          : rect.x;

      setCoords({ top, left });
    }

    setShow(!show);
  };

  const contextValue: DropdownContextValue = {
    buttonRef,
    inputRef,
    coords,
    menuRef,
    hideMenu,
    showMenu,
    onToggle,
    show,
    usePortal,
  };

  return (
    <DropdownContext.Provider value={contextValue}>
      <div className={styles.container}>{children}</div>
    </DropdownContext.Provider>
  );
};

Dropdown.Button = DropdownButton;
Dropdown.ButtonWithIcon = DropdownButtonWithIcon;
Dropdown.Input = DropDownInput;
Dropdown.Menu = DropdownMenu;
Dropdown.MenuItem = DropdownMenuItem;
Dropdown.MenuItemType = DropdownMenuItemType;
Dropdown.MenuItemDivider = DropdownMenuItemDivider;

export default Dropdown;
